import * as ReactDeviceDetect from "react-device-detect"

interface BodyScrollOptions {
    reserveScrollBarGap?: boolean
    allowTouchMove?: (el: EventTarget) => boolean
}

interface Lock {
    targetElement: HTMLElement
    options: BodyScrollOptions
}

// HEAVILY influenced by https://github.com/willmcpo/body-scroll-lock/

export class BodyScrollLock {
    private static locks: Lock[] = []
    private static documentListenerAdded = false
    private static initialClientY = -1
    private static previousBodyOverflowSetting: string | undefined
    private static previousBodyPosition:
        | { position: string; top: string; left: string }
        | undefined
    private static previousBodyPaddingRight: string | undefined
    private static hasPassiveEvents: boolean = (() => {
        let passive = false
        if (typeof window !== "undefined") {
            const passiveTestOptions = {
                get passive() {
                    passive = true
                    return undefined
                },
            }
            window.addEventListener(
                "testPassive" as any,
                null as any,
                passiveTestOptions,
            )
            window.removeEventListener("testPassive" as any, null as any)
        }
        return passive
    })()

    private static allowTouchMove(el: EventTarget): boolean {
        return BodyScrollLock.locks.some(
            (lock) =>
                lock.options.allowTouchMove && lock.options.allowTouchMove(el),
        )
    }

    private static preventDefault = (rawEvent: TouchEvent): boolean => {
        const e = rawEvent || (window.event as TouchEvent)

        // If the element is allowed to receive touchmove, skip.
        if (BodyScrollLock.allowTouchMove(e.target as EventTarget)) {
            return true
        }

        // Do not prevent if this is a multi-touch (e.g., pinch to zoom).
        if (e.touches.length > 1) return true

        if (e.preventDefault) e.preventDefault()

        return false
    }

    private static setOverflowHidden(options?: BodyScrollOptions): void {
        if (BodyScrollLock.previousBodyPaddingRight === undefined) {
            const reserveScrollBarGap =
                !!options && options.reserveScrollBarGap === true
            const scrollBarGap =
                window.innerWidth - document.documentElement.clientWidth

            if (reserveScrollBarGap && scrollBarGap > 0) {
                const computedBodyPaddingRight = parseInt(
                    window
                        .getComputedStyle(document.body)
                        .getPropertyValue("padding-right"),
                    10,
                )
                BodyScrollLock.previousBodyPaddingRight =
                    document.body.style.paddingRight
                document.body.style.paddingRight = `${computedBodyPaddingRight + scrollBarGap}px`
            }
        }

        if (BodyScrollLock.previousBodyOverflowSetting === undefined) {
            BodyScrollLock.previousBodyOverflowSetting =
                document.body.style.overflow
            document.body.style.overflow = "hidden"
        }
    }

    private static restoreOverflowSetting(): void {
        if (BodyScrollLock.previousBodyPaddingRight !== undefined) {
            document.body.style.paddingRight =
                BodyScrollLock.previousBodyPaddingRight
            BodyScrollLock.previousBodyPaddingRight = undefined
        }

        if (BodyScrollLock.previousBodyOverflowSetting !== undefined) {
            document.body.style.overflow =
                BodyScrollLock.previousBodyOverflowSetting
            BodyScrollLock.previousBodyOverflowSetting = undefined
        }
    }

    private static setPositionFixed(): void {
        window.requestAnimationFrame(() => {
            if (BodyScrollLock.previousBodyPosition === undefined) {
                BodyScrollLock.previousBodyPosition = {
                    position: document.body.style.position,
                    top: document.body.style.top,
                    left: document.body.style.left,
                }

                const { scrollY, scrollX, innerHeight } = window
                document.body.style.position = "fixed"
                document.body.style.top = `${-scrollY}px`
                document.body.style.left = `${-scrollX}px`

                setTimeout(
                    () =>
                        window.requestAnimationFrame(() => {
                            const bottomBarHeight =
                                innerHeight - window.innerHeight
                            if (bottomBarHeight && scrollY >= innerHeight) {
                                document.body.style.top = `${-(scrollY + bottomBarHeight)}px`
                            }
                        }),
                    300,
                )
            }
        })
    }

    private static restorePositionSetting(): void {
        if (BodyScrollLock.previousBodyPosition !== undefined) {
            const y = -parseInt(document.body.style.top || "0", 10)
            const x = -parseInt(document.body.style.left || "0", 10)

            document.body.style.position =
                BodyScrollLock.previousBodyPosition.position
            document.body.style.top = BodyScrollLock.previousBodyPosition.top
            document.body.style.left = BodyScrollLock.previousBodyPosition.left

            window.scrollTo(x, y)

            BodyScrollLock.previousBodyPosition = undefined
        }
    }

    private static isTargetElementTotallyScrolled(
        targetElement: HTMLElement,
    ): boolean {
        return targetElement
            ? targetElement.scrollHeight - targetElement.scrollTop <=
                  targetElement.clientHeight
            : false
    }

    private static handleScroll(
        event: TouchEvent,
        targetElement: HTMLElement,
    ): boolean {
        const clientY =
            event.targetTouches[0].clientY - BodyScrollLock.initialClientY

        if (BodyScrollLock.allowTouchMove(event.target as EventTarget)) {
            return false
        }

        if (targetElement && targetElement.scrollTop === 0 && clientY > 0) {
            return BodyScrollLock.preventDefault(event)
        }

        if (
            BodyScrollLock.isTargetElementTotallyScrolled(targetElement) &&
            clientY < 0
        ) {
            return BodyScrollLock.preventDefault(event)
        }

        event.stopPropagation()
        return true
    }

    /**
     * Disables body scroll for the given target element.
     */
    public static disableBodyScroll(
        targetElement: HTMLElement,
        options?: BodyScrollOptions,
    ): void {
        if (!targetElement) return

        if (
            BodyScrollLock.locks.some(
                (lock) => lock.targetElement === targetElement,
            )
        ) {
            return
        }

        const lock: Lock = {
            targetElement,
            options: options || {},
        }

        BodyScrollLock.locks = [...BodyScrollLock.locks, lock]

        if (ReactDeviceDetect.isIOS) {
            BodyScrollLock.setPositionFixed()
        } else {
            BodyScrollLock.setOverflowHidden(options)
        }

        if (ReactDeviceDetect.isIOS) {
            targetElement.ontouchstart = (event: TouchEvent) => {
                if (event.targetTouches.length === 1) {
                    BodyScrollLock.initialClientY =
                        event.targetTouches[0].clientY
                }
            }
            targetElement.ontouchmove = (event: TouchEvent) => {
                if (event.targetTouches.length === 1) {
                    BodyScrollLock.handleScroll(event, targetElement)
                }
            }

            if (!BodyScrollLock.documentListenerAdded) {
                document.addEventListener(
                    "touchmove",
                    BodyScrollLock.preventDefault,
                    BodyScrollLock.hasPassiveEvents
                        ? { passive: false }
                        : undefined,
                )
                BodyScrollLock.documentListenerAdded = true
            }
        }
    }

    /**
     * Enables body scroll for the given target element.
     */
    public static enableBodyScroll(targetElement: HTMLElement): void {
        if (!targetElement) return

        BodyScrollLock.locks = BodyScrollLock.locks.filter(
            (lock) => lock.targetElement !== targetElement,
        )

        if (ReactDeviceDetect.isIOS) {
            targetElement.ontouchstart = null
            targetElement.ontouchmove = null

            if (
                BodyScrollLock.documentListenerAdded &&
                BodyScrollLock.locks.length === 0
            ) {
                document.removeEventListener(
                    "touchmove",
                    BodyScrollLock.preventDefault,
                )
                BodyScrollLock.documentListenerAdded = false
            }

            BodyScrollLock.restorePositionSetting()
        } else {
            if (BodyScrollLock.locks.length === 0) {
                BodyScrollLock.restoreOverflowSetting()
            }
        }
    }

    /**
     * Clears all active body scroll locks.
     */
    public static clearAllBodyScrollLocks(): void {
        if (ReactDeviceDetect.isIOS) {
            BodyScrollLock.locks.forEach((lock) => {
                lock.targetElement.ontouchstart = null
                lock.targetElement.ontouchmove = null
            })

            if (BodyScrollLock.documentListenerAdded) {
                document.removeEventListener(
                    "touchmove",
                    BodyScrollLock.preventDefault,
                )
                BodyScrollLock.documentListenerAdded = false
            }

            BodyScrollLock.initialClientY = -1
            BodyScrollLock.restorePositionSetting()
        } else {
            BodyScrollLock.restoreOverflowSetting()
        }

        BodyScrollLock.locks = []
    }
}
